import React, { useEffect, useState } from 'react';
import { useKanban } from '../context/KanbanContext';
import { COLUMNS_ORDER, ESTADO_LABELS, EstadoTarea } from '../types';

interface WipLimitesPanelProps {
    onClose: () => void;
}

type ValoresInput = Record<EstadoTarea, string>;

const valoresVacios = (): ValoresInput => ({
    backlog: '',
    por_hacer: '',
    en_progreso: '',
    esperando_cliente: '',
    testing: '',
    produccion: ''
});

export const WipLimitesPanel: React.FC<WipLimitesPanelProps> = ({ onClose }) => {
    const { wipLimites, fetchWipLimites, actualizarWipLimite } = useKanban();
    const [loading, setLoading] = useState(true);
    const [valores, setValores] = useState<ValoresInput>(valoresVacios());
    const [guardandoEstado, setGuardandoEstado] = useState<EstadoTarea | null>(null);
    const [guardadoOk, setGuardadoOk] = useState<EstadoTarea | null>(null);

    useEffect(() => {
        (async () => {
            setLoading(true);
            await fetchWipLimites();
            setLoading(false);
        })();
    }, [fetchWipLimites]);

    useEffect(() => {
        const nuevos = valoresVacios();
        wipLimites.forEach(w => {
            if (w.limite !== null) {
                nuevos[w.estado] = String(w.limite);
            }
        });
        setValores(nuevos);
    }, [wipLimites]);

    const handleGuardar = async (estado: EstadoTarea) => {
        const valor = valores[estado].trim();
        const limite = valor === '' ? null : parseInt(valor, 10);

        if (limite !== null && (isNaN(limite) || limite < 0)) {
            return;
        }

        setGuardandoEstado(estado);
        const success = await actualizarWipLimite(estado, limite);
        setGuardandoEstado(null);

        if (success) {
            setGuardadoOk(estado);
            setTimeout(() => setGuardadoOk(prev => (prev === estado ? null : prev)), 1500);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
            <div
                className="bg-white rounded-2xl shadow-2xl w-full max-w-lg transform transition-all"
                onClick={e => e.stopPropagation()}
            >
                <div className="flex items-center justify-between p-5 border-b border-slate-100">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl flex items-center justify-center text-white text-lg">
                            ⚙️
                        </div>
                        <div>
                            <h2 className="text-lg font-semibold text-slate-800">Límites WIP</h2>
                            <p className="text-xs text-slate-500">Trabajo en progreso máximo por columna (vacío = sin límite)</p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                <div className="p-5 space-y-3">
                    {loading ? (
                        <div className="flex flex-col items-center justify-center h-32 gap-3">
                            <div className="relative">
                                <div className="w-10 h-10 border-4 border-slate-200 rounded-full"></div>
                                <div className="absolute inset-0 border-4 border-transparent border-t-blue-500 rounded-full animate-spin"></div>
                            </div>
                            <p className="text-slate-500 text-sm">Cargando límites...</p>
                        </div>
                    ) : (
                        COLUMNS_ORDER.map(estado => (
                            <div key={estado} className="flex items-center gap-3 bg-slate-50 rounded-xl p-3">
                                <span className="flex-1 text-sm font-medium text-slate-700">{ESTADO_LABELS[estado]}</span>
                                <input
                                    type="number"
                                    min={0}
                                    value={valores[estado]}
                                    onChange={(e) => setValores(prev => ({ ...prev, [estado]: e.target.value }))}
                                    placeholder="Sin límite"
                                    className="w-28 px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all text-center"
                                />
                                <button
                                    onClick={() => handleGuardar(estado)}
                                    disabled={guardandoEstado === estado}
                                    className={`px-3 py-2 text-xs font-medium rounded-lg transition-colors disabled:opacity-50 ${
                                        guardadoOk === estado
                                            ? 'bg-emerald-100 text-emerald-700'
                                            : 'bg-blue-600 text-white hover:bg-blue-700'
                                    }`}
                                >
                                    {guardandoEstado === estado ? '...' : guardadoOk === estado ? '✓ Guardado' : 'Guardar'}
                                </button>
                            </div>
                        ))
                    )}
                </div>

                <div className="flex items-center justify-end p-5 border-t border-slate-100 bg-slate-50/50 rounded-b-2xl">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 text-sm border border-slate-200 text-slate-700 rounded-xl hover:bg-slate-100 transition-colors font-medium"
                    >
                        Cerrar
                    </button>
                </div>
            </div>
        </div>
    );
};
